/**
 * Custom JavaScript extensions for the theme
 * This file is loaded before theme.js to allow overriding components
 */

/**
 * Extended ImageParallax component with configurable intensity and modes
 *
 * Attributes:
 *   data-intensity: 0.1 to 0.5 (default 0.15)
 *   data-mode: "default" | "zoom" | "gentle" | "fixed" (default: "default")
 *     - default: Scale + translate (crops image)
 *     - zoom: Scale only, no translate (minimal crop, subtle zoom effect)
 *     - gentle: Translate only using object-position (no crop, image must be taller)
 *     - fixed: Image stays fixed while content scrolls (no crop)
 */
class ImageParallaxCustom extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this.setupParallax();
    }
  }

  async setupParallax() {
    const mode = this.getAttribute("data-mode") || "default";

    // Fixed mode uses CSS only, no animation library needed
    if (mode === "fixed") {
      this.setupFixedMode();
      return;
    }

    const { scroll, animate } = await import("vendor");

    const intensity = parseFloat(this.getAttribute("data-intensity")) || 0.15;
    const isFirstSection = this.closest(".shopify-section")?.matches(":first-child");
    const img = this.querySelector("img");

    if (!img) return;

    let animationProps;

    switch (mode) {
      case "zoom":
        // Zoom only - subtle scale effect, minimal cropping
        const minScale = 1;
        const maxScale = 1 + (intensity * 0.5);
        animationProps = {
          transform: [`scale(${maxScale})`, `scale(${minScale})`]
        };
        break;

      case "gentle":
        // Object-position based - no cropping
        img.style.objectFit = "cover";
        img.style.height = "100%";
        const shift = intensity * 100;
        animationProps = {
          objectPosition: [`center ${50 - shift}%`, `center ${50 + shift}%`]
        };
        break;

      default:
        // Default mode: Scale + translate (original behavior with crop)
        const scale = 1 + intensity;
        const translate = (intensity * 100) / scale;
        animationProps = {
          transform: [
            `scale(${scale}) translateY(-${translate}%)`,
            `scale(${scale}) translateY(${translate}%)`
          ]
        };
        break;
    }

    scroll(
      animate(img, animationProps, { easing: "linear" }),
      {
        target: img,
        offset: [isFirstSection ? "start start" : "start end", "end start"]
      }
    );
  }

  async setupFixedMode() {
    const { scroll, animate } = await import("vendor");

    const section = this.closest(".shopify-section");
    const container = this.closest(".content-over-media");
    const img = this.querySelector("img");

    if (!img || !section || !container) return;

    // Get intensity for movement amount (default 0.15 = 15% of viewport)
    const intensity = parseFloat(this.getAttribute("data-intensity")) || 0.15;
    const movement = intensity * 50; // Convert to vh units (e.g., 0.3 = 15vh movement)

    // Set up the fixed image effect using clip-path
    Object.assign(container.style, {
      clipPath: "inset(0)",
      overflow: "hidden"
    });

    Object.assign(img.style, {
      position: "fixed",
      inset: "0",
      width: "100%",
      height: `calc(100vh + ${movement * 2}vh)`, // Extra height to allow movement
      objectFit: "cover",
      objectPosition: "center",
      marginTop: `-${movement}vh` // Center the extra height
    });

    const isFirstSection = section.matches(":first-child");

    // Animate the image position as section scrolls (same direction as scroll)
    scroll(
      animate(
        img,
        {
          transform: [`translateY(${movement}vh)`, `translateY(-${movement}vh)`]
        },
        { easing: "linear" }
      ),
      {
        target: section,
        offset: [isFirstSection ? "start start" : "start end", "end start"]
      }
    );
  }
}

// Register our custom component before theme.js loads
if (!window.customElements.get("image-parallax")) {
  window.customElements.define("image-parallax", ImageParallaxCustom);
}

/**
 * ScrollRotate component - rotates an element based on scroll position
 *
 * Attributes:
 *   data-rotation: Total degrees to rotate (default: 180)
 *   data-direction: "cw" (clockwise) or "ccw" (counter-clockwise) (default: "cw")
 *
 * Example:
 *   <scroll-rotate data-rotation="180" data-direction="cw">
 *     <svg>...</svg>
 *   </scroll-rotate>
 */
class ScrollRotate extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this.setupRotation();
    }
  }

  async setupRotation() {
    const { scroll, animate } = await import("vendor");

    const rotation = parseFloat(this.getAttribute("data-rotation")) || 180;
    const direction = this.getAttribute("data-direction") || "cw";
    const section = this.closest(".shopify-section") || this.closest("section");

    if (!section) return;

    const startRotation = direction === "ccw" ? rotation : 0;
    const endRotation = direction === "ccw" ? 0 : rotation;

    scroll(
      animate(
        this,
        { transform: [`rotate(${startRotation}deg)`, `rotate(${endRotation}deg)`] },
        { easing: "linear" }
      ),
      {
        target: section,
        offset: ["start end", "end start"]
      }
    );
  }
}

if (!window.customElements.get("scroll-rotate")) {
  window.customElements.define("scroll-rotate", ScrollRotate);
}

/**
 * RevealOnScroll component - reveals elements with animation when they enter viewport
 * Uses Motion library's inView + animate (same as theme's BlogPosts, CollectionBanner, etc.)
 *
 * Attributes:
 *   data-reveal-type: "fade-up" (default) | "fade" | "fade-left" | "fade-right"
 *   data-reveal-delay: Delay in ms before animation starts (default: 0)
 *   data-reveal-duration: Animation duration in seconds (default: 0.6)
 *   data-reveal-distance: Distance to travel in px (default: 30)
 *   data-reveal-stagger: For parent containers - stagger children by this ms (default: 0)
 *
 * Example:
 *   <reveal-on-scroll data-reveal-type="fade-up" data-reveal-delay="100">
 *     <h2>Content</h2>
 *   </reveal-on-scroll>
 *
 *   <!-- With staggered children -->
 *   <reveal-on-scroll data-reveal-stagger="100">
 *     <div>Child 1</div>
 *     <div>Child 2</div>
 *     <div>Child 3</div>
 *   </reveal-on-scroll>
 */
class RevealOnScroll extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this.setupReveal();
    } else {
      // Show immediately for reduced motion preference
      this.style.opacity = "1";
    }
  }

  async setupReveal() {
    const { inView, animate, stagger } = await import("vendor");

    const type = this.getAttribute("data-reveal-type") || "fade-up";
    const delay = parseFloat(this.getAttribute("data-reveal-delay") || "0") / 1000;
    const duration = parseFloat(this.getAttribute("data-reveal-duration") || "0.6");
    const distance = parseFloat(this.getAttribute("data-reveal-distance") || "30");
    const staggerDelay = parseFloat(this.getAttribute("data-reveal-stagger") || "0") / 1000;

    // Set initial state
    this.style.opacity = "0";

    // Build transform based on type
    let fromTransform, toTransform;
    switch (type) {
      case "fade":
        fromTransform = toTransform = "none";
        break;
      case "fade-left":
        fromTransform = `translateX(-${distance}px)`;
        toTransform = "translateX(0)";
        break;
      case "fade-right":
        fromTransform = `translateX(${distance}px)`;
        toTransform = "translateX(0)";
        break;
      case "fade-up":
      default:
        fromTransform = `translateY(${distance}px)`;
        toTransform = "translateY(0)";
        break;
    }

    inView(
      this,
      () => {
        // If stagger is set, animate children instead of self
        if (staggerDelay > 0 && this.children.length > 0) {
          this.style.opacity = "1";
          animate(
            this.children,
            {
              opacity: [0, 1],
              transform: [fromTransform, toTransform]
            },
            {
              duration,
              delay: stagger(staggerDelay, { easing: "ease-out" }),
              easing: [0.215, 0.61, 0.355, 1]
            }
          );
        } else {
          animate(
            this,
            {
              opacity: [0, 1],
              transform: [fromTransform, toTransform]
            },
            {
              duration,
              delay,
              easing: [0.215, 0.61, 0.355, 1]
            }
          );
        }
      },
      { margin: "-50px 0px" }
    );
  }
}

if (!window.customElements.get("reveal-on-scroll")) {
  window.customElements.define("reveal-on-scroll", RevealOnScroll);
}
